import { fmtPrice } from "../lib/format";
import { Rating, VerifiedBadge } from "../components/Rating";
import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n, catName, tr } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Star, MapPin, Search } from "lucide-react";

export default function ServicesPage() {
  const [params, setParams] = useSearchParams();
  const { lang, t } = useI18n();
  const [services, setServices] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [loc, setLoc] = useState(params.get("location") || "");
  const [cat, setCat] = useState(params.get("category_id") || "all");
  const [sort, setSort] = useState(params.get("sort") || "recent");

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  const load = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (loc) p.set("location", loc);
    if (cat && cat !== "all") p.set("category_id", cat);
    if (sort) p.set("sort", sort);
    setParams(p);
    api.get(`/services?${p.toString()}`).then((r) => setServices(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <h1 className="font-serif text-4xl mb-2">{t("explore_services")}</h1>
      <p className="text-stone-600 text-sm">{t("explore_sub")}</p>

      <div className="mt-8 bg-white border border-stone-200 rounded-2xl p-4 grid md:grid-cols-6 gap-3">
        <div className="md:col-span-2 flex items-center gap-2 border border-stone-200 rounded-xl px-3">
          <Search size={16} className="text-stone-400" />
          <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_ph")} className="border-0 shadow-none focus-visible:ring-0" />
        </div>
        <div className="flex items-center gap-2 border border-stone-200 rounded-xl px-3">
          <MapPin size={16} className="text-stone-400" />
          <Input data-testid="location-input" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder={t("city")} className="border-0 shadow-none focus-visible:ring-0" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger data-testid="category-filter" className="rounded-xl"><SelectValue placeholder={t("category")} /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">{t("all_cats")}</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.id}>{catName(c, lang)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="sort-filter" className="rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="recent">{t("sort_recent")}</SelectItem>
            <SelectItem value="price_asc">{t("sort_price_asc")}</SelectItem>
            <SelectItem value="price_desc">{t("sort_price_desc")}</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="apply-filters" onClick={load} className="rounded-xl bg-stone-900 hover:bg-stone-800 text-stone-50">{t("search_btn")}</Button>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.length === 0 && <div className="col-span-full text-center py-16 text-stone-500">{t("no_services")}</div>}
        {services.map((s) => (
          <Link key={s.id} to={`/services/${s.id}`} data-testid={`svc-${s.id}`} className="service-card block bg-white border border-stone-200/70 rounded-2xl overflow-hidden">
            <div className="aspect-[4/3] bg-stone-100"><img src={s.images?.[0]} alt={tr(s, "title", lang)} className="w-full h-full object-cover" /></div>
            <div className="p-5">
              <div className="eyebrow text-stone-500 text-xs">{catName(s.category, lang)}</div>
              <div className="font-serif text-xl mt-1 line-clamp-2">{tr(s, "title", lang)}</div>
              <div className="flex items-center gap-2 mt-3 text-sm">
                <span className="text-stone-600">{s.provider?.name}</span>
                <Rating rating={s.rating} count={s.review_count} />
              </div>
              <div className="flex items-center justify-between mt-3 text-sm">
                <div className="text-stone-500 flex items-center gap-1"><MapPin size={12} />{s.location}</div>
                <div className="font-serif text-lg">{fmtPrice(s.price)}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
